import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogIn, Mail, CheckCircle2, AlertCircle } from "lucide-react";

export default function Login() {
  const { user, login, requestMagicLink } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefilledEmail = params.get("email") || "";
  const returnTo = params.get("return_to") || "";
  const [email, setEmail] = useState(prefilledEmail);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(params.get("error"));
  const autoSubmitted = useRef(false);

  // If already signed in, bounce straight to the right dashboard (honouring ?return_to).
  useEffect(() => {
    if (!user) return;
    const dest = returnTo || (user.role === "admin" ? "/admin" : "/dashboard");
    navigate(dest, { replace: true });
  }, [user, navigate, returnTo]);

  // One-shot auto-submit: when arriving from the booking close-out with the
  // email already validated, skip the "click to send" step. The ref guard
  // prevents StrictMode double-mount + hot-reload re-fires.
  useEffect(() => {
    if (autoSubmitted.current) return;
    if (!prefilledEmail || user) return;
    autoSubmitted.current = true;
    (async () => {
      setBusy(true);
      try {
        await requestMagicLink(prefilledEmail);
        setSent(true);
      } catch (err) {
        setError(err?.response?.data?.detail || "Couldn't send the link. Please try again.");
      } finally {
        setBusy(false);
      }
    })();
  }, [prefilledEmail, user, requestMagicLink]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await requestMagicLink(email.trim());
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't send the link. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="max-w-md mx-auto px-6 py-16" data-testid="login-page">
      <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">&larr; Back to site</Link>
      <div className="mt-6">
        <div className="label-mono">Sign in</div>
        <h1 className="font-display text-4xl mt-3 tracking-tight">Welcome back.</h1>
        <p className="text-muted-foreground mt-3">
          {prefilledEmail
            ? "We've matched your booking to this email. Sign in to see it in your dashboard."
            : "Use Google for the fastest path, or get a one-click sign-in link via email."}
        </p>
      </div>

      {error === "invalid_link" && (
        <div className="mt-6 p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-800 flex items-start gap-2" data-testid="login-error">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          That sign-in link was already used or never existed. Request a fresh one below.
        </div>
      )}
      {error === "link_expired" && (
        <div className="mt-6 p-3 rounded-md border border-amber-200 bg-amber-50 text-sm text-amber-800 flex items-start gap-2" data-testid="login-error">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          That sign-in link expired. Magic links are valid for 15 minutes — request a fresh one below.
        </div>
      )}
      {error && error !== "invalid_link" && error !== "link_expired" && (
        <div className="mt-6 p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-800" data-testid="login-error">{error}</div>
      )}

      <Button
        onClick={login}
        className="mt-8 w-full rounded-sm h-11 bg-[#0A0A0A] hover:bg-black/85"
        data-testid="login-google"
      >
        <LogIn className="h-4 w-4 mr-2" /> Continue with Google
      </Button>

      <div className="my-7 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="flex-1 h-px bg-border" />
        <span className="uppercase tracking-widest">or</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {sent ? (
        <div className="p-5 rounded-md border border-green-200 bg-green-50 text-sm text-green-900" data-testid="login-sent">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-green-700" />
            <div>
              <div className="font-semibold">Check your inbox.</div>
              <p className="mt-1 text-green-900/85">
                If <b>{email}</b> matches an active engagement, you&apos;ll receive a one-click sign-in link within a minute. The link is valid for 15 minutes.
              </p>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                className="text-xs underline mt-3"
                data-testid="login-sent-reset"
              >
                Send to a different email
              </button>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="login-magic-form">
          <label className="block">
            <span className="label-mono">Email address</span>
            <div className="mt-2 flex items-center gap-2 border border-border rounded-sm px-3 focus-within:border-[#ac62ea]">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="flex-1 py-2.5 text-sm bg-transparent outline-none"
                required
                autoComplete="email"
                data-testid="login-email-input"
              />
            </div>
          </label>
          <Button
            type="submit"
            disabled={busy || !email.trim()}
            className="w-full rounded-sm h-11 bg-[#ac62ea] hover:bg-[#8a48cf]"
            data-testid="login-magic-submit"
          >
            {busy ? "Sending…" : "Send sign-in link"}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-4">
            No password required. We&apos;ll email you a one-click link.
          </p>
        </form>
      )}
    </section>
  );
}
